import { useRef, useContext, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import type { MediaOptions } from '@atlaskit/editor-plugin-media/types';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { mediaPlugin } from '@atlaskit/editor-plugin-media';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import { fg } from '@atlaskit/platform-feature-flags';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAttachmentSizeLimitErrorFlag } from '@confluence/fabric-media-support/entry-points/attachmentSizeLimitErrorHelpers.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	createEditorMediaProvider,
	createRendererMediaProvider,
	createEditorMediaProviderDynamic,
	createRendererMediaProviderDynamic,
} from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import { getMediaOptions } from '../utils/mediaOptions.ts';

export const useMediaOptions = ({
	contentId,
	contentType,
	editorAPI,
	flags,
	viewMode,
}: {
	contentId: string;
	contentType: string;
	editorAPI: PublicPluginAPI<[typeof mediaPlugin]> | undefined;
	flags?: FlagsStateContainer;
	viewMode?: boolean;
}): MediaOptions => {
	const intl = useIntl();
	const { featureFlags } = useSessionData();
	const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceKey = useSpaceKey();
	const ssrMediaConfig = useMediaSSRModeAndAuthConfig({
		contentId,
		spaceKey: spaceKey ?? undefined,
		isExternalShare,
	});

	const resolver = useRef<(provider: any) => void>();
	// Keep most recent option values so dynamic provider getters don't close over stale ones
	const latestParamsRef = useRef({ contentId, spaceKey, isExternalShare, contentType, viewMode });
	latestParamsRef.current = { contentId, spaceKey, isExternalShare, contentType, viewMode };
	const showAttachmentLimitReachedFlag = useCallback(
		(data: UploadRejectionData) => {
			if (flags && data) {
				void flags.showErrorFlag(getAttachmentSizeLimitErrorFlag(data, intl)).then(() => {
					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							name: 'nextEditorAttachmentTooLargeFlag',
						},
					}).fire();
				});
			}
			return !!data;
		},
		[flags, intl, createAnalyticsEvent],
	);

	useEffect(() => {
		if (!contentId) {
			return;
		}

		const createMediaProvider = () => {
			// Skip creating media provider in SSR environment
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				return;
			}

			if (viewMode) {
				return fg('confluence_frontend_transition_preloading_system')
					? createRendererMediaProviderDynamic(() => ({
							contentId: latestParamsRef.current.contentId,
							spaceKey: latestParamsRef.current.spaceKey,
							isExternalShare: latestParamsRef.current.isExternalShare,
						}))
					: createRendererMediaProvider({ contentId, spaceKey, isExternalShare });
			}
			if (contentType) {
				return fg('confluence_frontend_transition_preloading_system')
					? createEditorMediaProviderDynamic(() => ({
							contentId: latestParamsRef.current.contentId,
							spaceKey: latestParamsRef.current.spaceKey,
							contentType: latestParamsRef.current.contentType!,
							isExternalShare: latestParamsRef.current.isExternalShare,
							// Passing undefined will allow us to use the default atlaskit size limit error behavior
							onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
						}))
					: createEditorMediaProvider({
							contentId,
							spaceKey,
							contentType,
							isExternalShare,
							// Passing undefined will allow us to use the default atlaskit size limit error behavior
							onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
						});
			}
		};

		const mediaProvider = createMediaProvider();
		if (mediaProvider) {
			editorAPI?.media?.actions.setProvider(mediaProvider);
			if (resolver.current) {
				resolver.current(mediaProvider);
			}
		}
	}, [
		contentId,
		contentType,
		spaceKey,
		isExternalShare,
		viewMode,
		flags,
		showAttachmentLimitReachedFlag,
		featureFlags,
		editorAPI,
	]);

	const ssrMediaProvider = useMemo(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!ssrMediaConfig || !process.env.REACT_SSR) {
			return undefined;
		}

		return {
			viewMediaClientConfig: ssrMediaConfig.config,
		} as MediaProvider;
	}, [ssrMediaConfig]);

	// IMPORTANT: If you change the code below, please also update the
	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/mediaPluginOptions/mediaPluginOptions.ts
	return useMemo(
		() =>
			getMediaOptions({
				mediaProvider: new Promise((resolve) => (resolver.current = resolve)),
				ssrMediaProvider,
				intl,
				featureFlags,
			}),
		[resolver, intl, featureFlags, ssrMediaProvider],
	);
};
